<script
  setup
  lang="ts"
  generic="
    T extends KaComboboxOptionProps | KaComboboxGroupProps | null | undefined =
      KaComboboxOptionProps | KaComboboxGroupProps | null | undefined,
    M extends boolean = false
  "
>
import { reactiveOmit } from '@vueuse/core';
import { mergeProps, useAttrs, useSlots } from 'vue';

import type { KaVeeFormGroupProps } from '@/kasten-components/components/KaVeeFormField';
import { KaVeeFormField } from '@/kasten-components/components/KaVeeFormField';

import type { KaCombobox2Emits, KaComboboxProps } from './KaCombobox.vue';
import KaCombobox from './KaCombobox.vue';
import type { KaComboboxGroupProps } from './KaComboboxGroup.vue';
import type { KaComboboxOptionProps } from './KaComboboxOption.vue';

type KaComboboxVeeProps<
  T extends KaComboboxOptionProps | KaComboboxGroupProps | null | undefined,
  M extends boolean,
> = KaComboboxProps<T, M> &
  Pick<
    KaVeeFormGroupProps,
    'rules' | 'validateOnMount' | 'bails' | 'standalone' | 'keepValueOnUnmount' | 'mode'
  >;

const emit = defineEmits<KaCombobox2Emits<T, M>>();

defineOptions({
  name: 'KaComboboxVee',
  inheritAttrs: false,
});

const props = withDefaults(defineProps<KaComboboxVeeProps<T, M>>(), {
  mode: 'passive-aggressive',
  keepValueOnUnmount: true,
});

const attrs = useAttrs();
const slots = useSlots();

// Strip vee-validate-only props so they are not forwarded to KaCombobox as unknown attributes.
const comboboxProps = reactiveOmit(
  props,
  'rules',
  'validateOnMount',
  'bails',
  'standalone',
  'keepValueOnUnmount',
  'mode',
);

// `mergeProps` combines all sources and — unlike a plain spread — merges event listeners
// (e.g. `onChange`) so that both the caller's handler and vee-validate's internal handler
// are called, instead of the last one silently overwriting the earlier ones resulting in
// vee-validate not working. The typed wrapper is needed because `mergeProps` returns
// `Record<string, unknown>`, which Vue's template type-checker cannot verify against
// `KaComboboxProps<T, M>` directly.
function mergedComboboxProps(...sources: Parameters<typeof mergeProps>): KaComboboxProps<T, M> {
  return mergeProps(...sources) as KaComboboxProps<T, M>;
}
</script>

<template>
  <KaVeeFormField
    v-slot="formGroupProps"
    :name="props.name"
    :rules="props.rules"
    :validate-on-mount="props.validateOnMount"
    :bails="props.bails"
    :standalone="props.standalone"
    :keep-value-on-unmount="props.keepValueOnUnmount"
    :mode="props.mode"
  >
    <KaCombobox
      v-bind="
        mergedComboboxProps({ ...(comboboxProps as KaComboboxProps<T, M>) }, formGroupProps, attrs)
      "
      @change="$emit('change', $event)"
      @open="$emit('open')"
      @close="$emit('close')"
    >
      <template v-for="(_, name) in slots" :key="name" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}" />
      </template>
    </KaCombobox>
  </KaVeeFormField>
</template>
